import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface CardMenuItem {
  /**
   * Stable, locale-independent identity for the item. Drives both the React
   * `key` and the `card-menu-item-<id>` test id, which used to be derived from
   * `label` — so translating "Duplicate"/"Delete" would have silently renamed
   * every selector under a non-en locale and coupled test ids to UI copy
   * (#154). Keep these ids equal to the old en-derived slugs.
   */
  id: string
  label: string
  onSelect: () => void
  danger?: boolean
}

interface CardMenuProps {
  items: CardMenuItem[]
}

/**
 * Kebab (⋮) button + small dropdown menu for gallery cards.
 * - Closes on Escape (restores trigger focus) or outside click.
 * - Standard role=menu keyboard contract: opening moves focus to the first
 *   menuitem; ArrowDown/ArrowUp cycle through items.
 * - aria-haspopup + aria-expanded on the trigger button.
 * - kebab click does NOT navigate the card (stopPropagation + preventDefault).
 */
export function CardMenu({ items }: CardMenuProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Standard role=menu focus contract: move focus to the first menuitem on
  // open so keyboard users can immediately arrow through the items.
  useEffect(() => {
    if (!open) return
    const first = containerRef.current?.querySelector<HTMLElement>('[role="menuitem"]')
    first?.focus()
  }, [open])

  // Close on outside click. Also enforces the single-open invariant: opening
  // a second card's menu fires mousedown outside this one, closing it first.
  useEffect(() => {
    if (!open) return
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open])

  // Close on Escape; also handle arrow key navigation
  useEffect(() => {
    if (!open) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const container = containerRef.current
        if (!container) return
        const btns = Array.from(container.querySelectorAll<HTMLElement>('[role="menuitem"]'))
        if (btns.length === 0) return
        const idx = btns.indexOf(document.activeElement as HTMLElement)
        if (e.key === 'ArrowDown') {
          btns[(idx + 1) % btns.length]?.focus()
        } else {
          btns[(idx - 1 + btns.length) % btns.length]?.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open])

  return (
    <div ref={containerRef} className="relative" data-testid="card-menu">
      <button
        ref={triggerRef}
        type="button"
        data-testid="card-menu-trigger"
        aria-label={t('cardMenu.actions')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        className="flex h-6 w-6 items-center justify-center rounded text-stone-400 hover:bg-stone-100 hover:text-stone-700 dark:hover:bg-stone-700 dark:hover:text-stone-200"
      >
        ⋮
      </button>

      {open && (
        <div
          role="menu"
          data-testid="card-menu-dropdown"
          className="absolute right-0 top-full z-20 mt-1 min-w-[120px] rounded-md border border-stone-200 bg-white py-1 shadow-lg dark:border-stone-700 dark:bg-stone-900"
        >
          {items.map((item) => (
            <button
              key={item.id}
              role="menuitem"
              type="button"
              data-testid={`card-menu-item-${item.id}`}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setOpen(false)
                item.onSelect()
              }}
              className={[
                'w-full px-3 py-1.5 text-left text-sm',
                item.danger
                  ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950'
                  : 'text-stone-700 hover:bg-stone-50 dark:text-stone-200 dark:hover:bg-stone-800',
              ].join(' ')}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
